import { FC, useMemo } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import type { StepThreeFormSchema } from '../../../validations/add-template.validation';
import type { UpdateTemplateFormSchema } from '../../../validations/edit-template.validation';
import type { EditTemplateFormProps } from './edit-template-form';

import { EditIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { StorageDocumentLink } from '../../../../../../components/storage-document-link/index';
import { SummaryItem } from '../../../../../../components/summary-list/index';
import { useLocale } from '../../../../../../hooks/index';
import { VariableMappingType } from '../../../enums/mapping-editor.const';
import { PopupType } from '../../../enums/popup-type.const';
import {
  categoryLabelKey,
  expirationPeriodLabelKey,
  subCategoryLabelKey,
} from '../../../enums/template.const';
import { MappingEditorStore } from '../../../store/mapping-editor.store';
import { TemplatePopupStore } from '../../../store/template-popup.store';
import { TemplateStep } from '../../../types/template.types';
import { cloneStepThreeSnapshot } from '../../../utils/clone-step-three-snapshot.util';
import { getFieldMappingDisplayLabel } from '../../../utils/field-mapping-display-label.util';
import { resolveFieldOptions } from '../../../utils/resolve-field-options.util';
import {
  getStepThreeFile,
  getStepThreeFileLink,
} from '../../../validations/add-template.validation';
import { MapFieldsModal } from '../map-fields-modal/map-fields-modal';

export type EditTemplateSummaryViewProps = {
  control: Control<UpdateTemplateFormSchema>;
  data: EditTemplateFormProps['data'];
  getValues: UseFormGetValues<UpdateTemplateFormSchema>;
  goToSteps: (step: TemplateStep) => void;
  handleClose: () => void;
  handleSaveClick: () => void;
  isDirty: boolean;
  isValid: boolean;
  setValue: UseFormSetValue<UpdateTemplateFormSchema>;
};

export const EditTemplateSummaryView: FC<EditTemplateSummaryViewProps> = ({
  control,
  data,
  getValues,
  goToSteps,
  handleClose,
  handleSaveClick,
  isDirty,
  isValid,
  setValue,
}) => {
  const openPopup = TemplatePopupStore(({ openPopup }) => openPopup);
  const popupOpen = TemplatePopupStore(({ open }) => open);
  const popupType = TemplatePopupStore(({ type }) => type);
  const handlePopupDismiss = TemplatePopupStore(({ handleDismiss }) => handleDismiss);

  const { messagesLocale, messagesLocaleFn } = useLocale();
  const templateContext = MappingEditorStore(({ templateContext }) => templateContext);
  const variableMappingTypes = MappingEditorStore(
    ({ variableMappingTypes }) => variableMappingTypes,
  );
  const fieldMappingRaw = useWatch({ control, name: 'stepThree.fieldMapping', defaultValue: {} });
  const fieldMappingTypesForm = (useWatch({
    control,
    name: 'stepThree.fieldMappingTypes',
    defaultValue: {},
  }) ?? {}) as Partial<Record<string, VariableMappingType>>;
  const fieldOptions = useMemo(() => resolveFieldOptions(templateContext), [templateContext]);
  const stepThreeFile = useWatch({ control, name: 'stepThree.file' });

  const fieldMappingRows = useMemo(() => {
    const fm = (fieldMappingRaw ?? {}) as Record<string, string>;
    return Object.entries(fm).filter(([, v]) => (v?.trim?.() ?? '') !== '');
  }, [fieldMappingRaw]);
  const currentFile = getStepThreeFile(stepThreeFile);
  const currentFileLink = getStepThreeFileLink(stepThreeFile) || data.file.documentLink;
  const currentDocumentName = currentFile?.name || data.file.documentName;
  // A file picked in step 3 but not yet saved has no server-side scope entry, so it
  // must be served from the local File rather than through the presigned-get endpoint.
  const isNewUpload = Boolean(currentFile) && currentFileLink !== data.file.documentLink;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-100">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 py-4">
        <div className="flex flex-col">
          <div className="flex h-9 items-center justify-between rounded-t-lg bg-gray-50 px-4">
            <span className="text-sm font-semibold text-gray-700">
              <AppFormattedMessage id="DOCUMENT" />
            </span>
            <button
              className="rounded p-1 transition-colors hover:bg-gray-200"
              onClick={() => goToSteps(1)}
              type="button"
            >
              <EditIcon className="h-4 w-4" iconColor="#2565C8" />
            </button>
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="DOCUMENT_NAME" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={data.document.documentName}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={<AppFormattedMessage id={categoryLabelKey[data.document.category]} />}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SUB_CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={<AppFormattedMessage id={subCategoryLabelKey[data.document.subCategory]} />}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="EQUITY_PLAN" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={data.document.equityPlanLabel}
            />
            {data.document.expirationPeriod && (
              <SummaryItem
                className="py-3"
                title={<AppFormattedMessage id="EXPIRATION_PERIOD" />}
                titleClassName="w-fit"
                titleWrapperClassName="w-[120px]"
                value={
                  <AppFormattedMessage
                    id={expirationPeriodLabelKey[data.document.expirationPeriod]}
                  />
                }
              />
            )}
          </div>
        </div>

        <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white px-4">
          <div className="flex flex-row items-center">
            <SummaryItem
              className="flex-1"
              title={<AppFormattedMessage id="SIGNATORIES" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={data.document.signatoriesLabel}
            />
            <button
              className="rounded p-1 transition-colors hover:bg-gray-200"
              onClick={() => goToSteps(2)}
              type="button"
            >
              <EditIcon className="h-4 w-4" iconColor="#2565C8" />
            </button>
          </div>
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="SIGNING_MODE" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={data.document.signingModeLabel}
          />
          {data.document.isSignOrderMatters && data.document.signatoriesOrderLines.length > 0 && (
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SIGNATORIES_ORDER" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={
                <ul className="list-none pl-0 text-left">
                  {data.document.signatoriesOrderLines.map((line) => (
                    <li className="text-xs text-gray-700" key={line}>
                      {line}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </div>

        <div className="flex flex-col rounded-lg border border-gray-100 bg-white px-4">
          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="flex w-[120px] flex-shrink-0">
              <span className="text-xs font-[450] text-gray-500">
                <AppFormattedMessage id="DOCUMENTS_LABEL" />:
              </span>
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <StorageDocumentLink
                className="h-auto min-w-0 flex-1 cursor-pointer justify-start overflow-hidden p-0 text-left text-xs font-[450] text-brand-700 underline hover:no-underline"
                label={currentDocumentName}
                link={currentFileLink}
                localFile={isNewUpload ? currentFile : undefined}
              />
              <button
                className="shrink-0 rounded p-1 transition-colors hover:bg-gray-200"
                onClick={() => goToSteps(3)}
                type="button"
              >
                <EditIcon className="h-4 w-4" iconColor="#2565C8" />
              </button>
            </div>
          </div>
          <SummaryItem
            className="pb-2"
            title={<AppFormattedMessage id="MAPPED_FIELDS" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={String(fieldMappingRows.length)}
          />
        </div>

        <section className="flex flex-col rounded-lg bg-gray-50">
          <div className="flex h-9 items-center justify-between rounded-t-lg bg-gray-50 px-4">
            <span className="text-sm font-semibold text-gray-700">
              <AppFormattedMessage id="FIELD_MAPPINGS" />
            </span>
            <button
              className="rounded p-1 transition-colors hover:bg-gray-200"
              onClick={() =>
                openPopup({
                  mapFieldsStepThreeSnapshot: cloneStepThreeSnapshot(
                    getValues('stepThree') as StepThreeFormSchema,
                  ),
                  onAcceptCallback: () => {},
                  type: PopupType.MAP_FIELDS,
                })
              }
              type="button"
            >
              <EditIcon className="h-4 w-4" iconColor="#2565C8" />
            </button>
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
            {fieldMappingRows.length === 0 ? (
              <div className="py-3 text-xs font-[450] text-gray-500">—</div>
            ) : (
              fieldMappingRows.map(([placeholder, storedValue]) => (
                <div className="flex gap-3 py-3" key={placeholder}>
                  <span className="w-[140px] flex-shrink-0 truncate text-xs font-[450] text-gray-500">
                    {`{{${placeholder}}}`}
                  </span>
                  <span className="text-xs font-[450] text-gray-700 [overflow-wrap:anywhere]">
                    {getFieldMappingDisplayLabel({
                      fieldOptions,
                      mappingTypeExplicit: fieldMappingTypesForm[placeholder],
                      messagesLocale,
                      messagesLocaleFn,
                      storedValue,
                      variableMappingTypeFromStore: variableMappingTypes[placeholder],
                    })}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {popupOpen && popupType === PopupType.MAP_FIELDS && (
        <MapFieldsModal
          canSave={isValid}
          control={control}
          hasUnsavedChanges={isDirty}
          initialFieldMapping={getValues().stepThree?.fieldMapping ?? {}}
          initialFieldMappingTypes={getValues().stepThree?.fieldMappingTypes ?? {}}
          initialTabs={getValues().stepThree?.tabs ?? []}
          isOpen
          mode="update"
          onClose={handlePopupDismiss}
          onUpdate={handleSaveClick}
          setValue={setValue}
        />
      )}

      <div className="flex justify-start px-6 py-6">
        <button
          className="text-sm font-medium text-gray-700 hover:text-gray-900"
          onClick={handleClose}
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </button>
      </div>
    </div>
  );
};
